import { Component, OnInit } from '@angular/core';
import { JobBoardService } from '../../../../../shared/services/job-board.service';
import { IJobBoardSearchFilters, IJobs } from '../job-board.interface';
import { constants } from '../../../../../shared/constants/constants';
import { PipesModule } from '../../../../../shared/pipes/pipes.module';
import { TooltipModule } from 'primeng/tooltip';
import { DynamicDialogModule } from 'primeng/dynamicdialog';
import { CommonModule } from '@angular/common';
import { TableModule } from 'primeng/table';
import { InputTextModule } from 'primeng/inputtext';
import { HeadingComponent } from '../../../../../shared/components/heading/heading.component';
import { InputIconModule } from 'primeng/inputicon';
import { IconFieldModule } from 'primeng/iconfield';
import { ChipModule } from 'primeng/chip';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DropdownModule } from 'primeng/dropdown';
import { CheckboxModule } from 'primeng/checkbox';
import { ActivatedRoute, Router } from '@angular/router';
import { UserService } from '../../../../../shared/services/user.service';
import { UserClientVm } from '../../../../../store/user/user.interface';
import { ConfirmationDialogService } from '../../../../../shared/services/confirmation-dialog.service';
import { ToastService } from '../../../../../shared/services/toast.service';
import { HelperService } from '../../../../../shared/services/helper.service';

interface SortOption {
  name: string;
  field: string;
  defaultDirection: 'asc' | 'desc';
}

@Component({
    selector: 'app-job-listing',
    standalone: true,
    imports: [
        DropdownModule,
        CheckboxModule,
        ButtonModule,
        FormsModule,
        ChipModule,
        IconFieldModule,
        InputIconModule,
        HeadingComponent,
        InputTextModule,
        TableModule,
        CommonModule,
        TooltipModule,
        PipesModule
    ],
    templateUrl: './job-listing.component.html',
    styleUrl: './job-listing.component.scss'
})
export class JobListingComponent implements OnInit {
  
  user!: UserClientVm;
  jobs: IJobs[] = [];
  jobsCount: number = 0;
  jobStatus!: string;
  jobStatusTitle!: string;
  jobStatuses = constants.jobBoardTypes;
  headingButton: any = {
    url: '/client/jobs/create',
    label: 'create job',
  };
  
  statusMap: Record<string, string[]> = {
    ALL: [
      constants.jobBoardTypes.PUBLISHED,
      constants.jobBoardTypes.UNPUBLISHED,
      constants.jobBoardTypes.ARCHIVED,
      constants.jobBoardTypes.APPROVAL_PENDING,
      constants.jobBoardTypes.DENIED
    ],
    PUBLISHED: [constants.jobBoardTypes.PUBLISHED],
    UNPUBLISHED: [
      constants.jobBoardTypes.UNPUBLISHED,
      constants.jobBoardTypes.APPROVAL_PENDING,
      constants.jobBoardTypes.DENIED
    ],
    ARCHIVED: [constants.jobBoardTypes.ARCHIVED]
  };
  
  searchText!: string;
  
  // Updated sorting options
  sortOptions: SortOption[] = [
  
    { name: 'All', field: 'all', defaultDirection: 'desc' },
    { name: 'Name', field: 'title', defaultDirection: 'asc' },
    { name: 'Start Date', field: 'startDate', defaultDirection: 'desc' },
     { name: 'Publish Date', field: 'publishedAt', defaultDirection: 'desc' }
  ];
  
  // Traditional properties for reactive state management
  selectedSortOption: SortOption = this.sortOptions[0]; // Default to first option
  sortDirection: 'asc' | 'desc' = 'desc'; // Default direction
 
    filters = [
     { name: 'All', value: 'all', defaultDirection: 'desc' },
    { name: 'Name', value: 'title', defaultDirection: 'asc' },
    { name: 'Start Date', value: 'startDate', defaultDirection: 'desc' },
     { name: 'Publish Date', value: 'publishedAt', defaultDirection: 'desc' },

      { name: 'Rate', value: 'rate' , defaaultDirection: 'desc'},
      { name: 'Description', value: 'description' , defaultDirection: 'desc' }
  ];

  selectedFilter = this.filters[0]; // Traditional property
  
  // Getter for sort display text
  get sortDisplayText(): string {
    const direction = this.sortDirection;
    const directionText = direction === 'asc' ? 'Ascending' : 'Descending';
    const filterValue = this.selectedFilter.name;
    return `${filterValue} (${directionText})`;
  }

  // Getter for sort value
  get sortValue(): string {
    const direction = this.sortDirection;
    return direction === 'desc' ? `-1` : '1';
  }
  
  searchFilters!: IJobBoardSearchFilters;
  rateFilterOptions = [
    { label: 'Not Specified', value: 'not_specified'},
    { label: 'Depends on Experience', value: 'depends_on_experience'}
  ];
  selectedRateOptions: string[] = [];
  showRateFilters = false;

  constructor(
    private jobBoardService: JobBoardService,
    private routeParams: ActivatedRoute,
    private userService: UserService,
    private router: Router,
    private confirmationDialogService: ConfirmationDialogService,
    private toastService: ToastService,
    public helperService: HelperService,
  ) { }

  ngOnInit(): void {
    this.user = this.userService.getSessionUser();
    this.routeParams.url.subscribe((value) => {
      this.jobStatus = value[0].path.toUpperCase();
      this.jobStatusTitle =
        this.jobStatus[0].toUpperCase() +
        this.jobStatus.substring(1).toLowerCase() +
        ' Jobs';
    });
    this.resetFilter();
    // Initialize default sort direction when component loads
    this.sortDirection = this.selectedSortOption.defaultDirection;
  }

  resetFilter(): void {
    this.searchFilters = {
      limit: 10,
      status: this.statusMap[this.jobStatus] || [],
      firmId: this.user.firm._id,
      sortBy: ''
    };
  }

  async getJobs(event?: any): Promise<void> {
    this.resetFilter();

    if (event) {
      this.searchFilters.limit = event.rows;
      this.searchFilters.offset = event.first;
    }

    // Use selectedFilter as traditional property
    const currentFilter = this.selectedFilter;

    if (this.searchText && (!currentFilter.value || currentFilter.value === 'all')) {
      this.searchFilters.search = this.searchText;
    }

    if (currentFilter.value === 'rate') {
      this.searchFilters.notSpecified = this.selectedRateOptions.includes('not_specified');
      this.searchFilters.byExperience = this.selectedRateOptions.includes('depends_on_experience');
    }

    // Use getter for sort value
    this.searchFilters.sort = this.sortValue;

    if (currentFilter.value && currentFilter.value !== 'all' && !(this.searchFilters.notSpecified || this.searchFilters.byExperience)) {
      this.searchFilters.searchText = this.searchText;
      this.searchFilters.sortBy = currentFilter.value;
      (this.searchFilters as any)[currentFilter.value] = this.searchText;
    }

    const jobListings = await this.jobBoardService.getJobBoardListings(this.searchFilters);
    this.jobs = jobListings.slice(0, -1);
    this.jobsCount = jobListings[jobListings.length - 1]?.totalCount ?? 0;
  }

  onEnter($event: any) {
    if ($event.keyCode === 13) {
      this.getJobs();
    }
  }

  onSortOptionChange(event: any) {
    const newSortOption = event.value;
    this.selectedSortOption = newSortOption;
    // Set default direction for the new sort option
    this.sortDirection = newSortOption.defaultDirection;
    this.getJobs();
  }

  toggleSortDirection() {
    const currentDirection = this.sortDirection;
    const newDirection = currentDirection === 'asc' ? 'desc' : 'asc';
    this.sortDirection = newDirection;
    this.getJobs();
  }

  onFilterChange(event: any) {
    this.selectedFilter = event.value;
    this.sortDirection = event.value.defaultDirection;
    if (this.selectedFilter.value === 'rate') {
      this.showRateFilters = true;
      this.searchText = '';
    } else {
      this.showRateFilters = false;
    }
    
    this.getJobs();
  }

  onRateFilterChange() {
    this.searchText = '';
    this.getJobs();
  }

  isSearchDisabled(): boolean {
    if (this.selectedFilter.value === 'rate') {
       return this.selectedRateOptions.length > 0;
    }
    return false;
  }

  onEditJob($event: Event, $job: any) {
    $event.stopPropagation();
    this.router.navigate([`/client/jobs/edit/${$job._id}`]);
  }

  goToJobDetails(job: any) {
    this.router.navigateByUrl(
      `/client/jobs/${job._id}/details`,
      {
        state: {
          data: job
        }
      }
    );
  }

  onArchiveJob($event: Event, job: any) {
    $event.stopPropagation();
    this.confirmationDialogService.confirm({
      message: `Are you sure you want to archive ${job.title}?`,
      header: 'Confirmation',
      icon: 'pi pi-exclamation-triangle',
      acceptIcon: "none",
      rejectIcon: "none",
      rejectButtonStyleClass: "p-button-text",
      accept: () => {
        this.jobBoardService.updateJob({
          ...job,
          status: constants.jobBoardTypes.ARCHIVED
        }).then(() => {
          this.getJobs();
          this.toastService.showSuccess('Job archived successfully.');
        });
      }
    });
  }

  onPublishJob($event: Event, job: any) {
    $event.stopPropagation();
    this.confirmationDialogService.confirm({
      message: `You are about to submit this job posting for approval. Are you sure you want to continue?`,
      header: 'Confirmation',
      icon: 'pi pi-exclamation-triangle',
      acceptIcon: "none",
      rejectIcon: "none",
      rejectButtonStyleClass: "p-button-text",
      accept: () => {
        this.jobBoardService.updateJob({
          ...job,
          status: constants.jobBoardTypes.PUBLISHED,
          publishedAt: new Date(),
          published: true,
          approved: true
        }).then(() => {
          this.getJobs();
          this.toastService.showSuccess('Job published successfully.');
        });
      }
    });
  }

  onRestoreJob($event: Event, job: any) {
    $event.stopPropagation();
    this.confirmationDialogService.confirm({
      message: `Are you sure you want to restore ${job.title}?`,
      header: 'Confirmation',
      icon: 'pi pi-exclamation-triangle',
      acceptIcon: "none",
      rejectIcon: "none",
      rejectButtonStyleClass: "p-button-text",
      accept: () => {
        this.jobBoardService.updateJob({
          ...job,
          status: constants.jobBoardTypes.UNPUBLISHED
        }).then(() => {
          this.getJobs();
          this.toastService.showSuccess('Job restored successfully.');
        });
      }
    });
  }
}
